import { AuthService } from './AuthService';
import keycloak from '../../Config/KeyCloakConfig';
import { AppUser } from '../../Models/AppUser';
import httpService from '../httpService';
import { ApiEndpoints } from '../../constants/appConstants';
import { SessionStorageService } from '../SessionStorageService';
import logger from '../../utils/logger';
import { notifyUserLogout } from './logoutUser';

export class KeycloakAuthService implements AuthService {
    private refreshIntervalId: ReturnType<typeof setInterval> | null = null;

    private static keycloakInitialized = false;

    private getSecureRedirectUri(): string {
        return window.location.origin;
    }

    async login(username?: string, password?: string): Promise<void> {
        if (!username || !password) {
            throw new Error('Username and password are required for Keycloak login.');
        }

        try {
            const keycloakLoginUrl =
                `${import.meta.env.VITE_KEYCLOAK_URL as string}${keycloak.realm}/protocol/openid-connect/token`;

            await httpService.post(keycloakLoginUrl, {
                grant_type: 'password',
                client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string,
                username,
                password,
            });
        } catch (error) {
            logger.error('KeycloakAuthService.login failed', error);
            throw error;
        }
    }

    async loginWithoutCredentials(): Promise<boolean> {
        try {
            if (keycloak.authenticated) {
                SessionStorageService.setAuthToken(keycloak.token ?? '');
                return true;
            }

            if (KeycloakAuthService.keycloakInitialized) {
                return keycloak.authenticated ?? false;
            }

            const token = SessionStorageService.getKeycloakToken();
            const refreshToken = SessionStorageService.getKeycloakRefreshToken();

            if (!keycloak.authenticated && !KeycloakAuthService.keycloakInitialized) {
                KeycloakAuthService.keycloakInitialized = true;

                const isDevelopment =
                    window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1';

                const result = await keycloak.init({
                    onLoad: 'login-required',
                    pkceMethod: 'S256',
                    checkLoginIframe: !isDevelopment,
                    silentCheckSsoRedirectUri: !isDevelopment
                        ? `${window.location.origin}/silent-check-sso.html`
                        : undefined,
                    token: token || '',
                    refreshToken: refreshToken || '',
                });

                if (result) {
                    this.scheduleTokenRefresh(keycloak);
                    SessionStorageService.setAuthToken(keycloak.token ?? '');
                    return true;
                }

                await keycloak.login({
                    redirectUri: this.getSecureRedirectUri(),
                });

                return result ?? false;
            }

            return false;
        } catch (error) {
            KeycloakAuthService.keycloakInitialized = false;
            logger.error(
                'KeycloakAuthService.loginWithoutCredentials failed',
                error
            );
            throw error;
        }
    }

    async logout(forceLogout: boolean = false): Promise<void> {
        KeycloakAuthService.keycloakInitialized = false;

        try {
            await notifyUserLogout();

            if (forceLogout) {
                await this.forceKeycloakLogout();
                return;
            }

            this.clearTokenRefresh();
            SessionStorageService.clearAllTokens();

            await keycloak.logout();

            logger.info('Keycloak user logged out successfully');
        } catch (error) {
            logger.error('KeycloakAuthService.logout failed', error);
            throw error;
        }
    }

    getUser(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            keycloak
                .loadUserInfo()
                .then(async (user: any) => {
                    try {
                        await this.getAuthorizationToken();

                        const authorizationToken =
                            SessionStorageService.getAuthorizationToken() ?? '';

                        const roles =
                            this.getRolesFromAuthorizationToken(
                                authorizationToken
                            );

                        const appUser: AppUser = {
                            name: user.name ?? '',
                            email: user.email ?? '',
                            groups: roles,
                        };

                        resolve(appUser);
                    } catch (error) {
                        logger.error(
                            'KeycloakAuthService.getUser failed while processing user information',
                            error
                        );
                        reject(error);
                    }
                })
                .catch((error) => {
                    logger.error(
                        'KeycloakAuthService.getUser failed to load user information',
                        error
                    );
                    reject(error);
                });
        });
    }

    async isAuthenticated(): Promise<boolean> {
        try {
            return keycloak.authenticated ?? false;
        } catch (error) {
            logger.error(
                'KeycloakAuthService.isAuthenticated failed',
                error
            );
            return false;
        }
    }

    async hasActiveSession(): Promise<boolean> {
        try {
            return await this.loginWithoutCredentials();
        } catch (error) {
            logger.error(
                'KeycloakAuthService.hasActiveSession failed',
                error
            );
            return false;
        }
    }

    scheduleTokenRefresh(keycloakInstance: any): void {
        this.clearTokenRefresh();

        this.refreshIntervalId = setInterval(() => {
            try {
                const expiration = keycloakInstance.tokenParsed?.exp;

                if (!expiration) {
                    logger.warn(
                        'Keycloak token expiration is unavailable'
                    );
                    return;
                }

                const beforeExpire =
                    expiration * 1000 -
                    Date.now() -
                    70 * 1000;

                keycloakInstance
                    .updateToken(beforeExpire)
                    .then(() => {
                        SessionStorageService.setAuthToken(
                            keycloakInstance.token ?? ''
                        );
                    })
                    .catch((error: any) => {
                        logger.error(
                            'KeycloakAuthService.scheduleTokenRefresh failed to refresh token',
                            error
                        );

                        this.clearTokenRefresh();
                        SessionStorageService.clearAllTokens();

                        keycloakInstance.logout().catch((logoutError: any) => {
                            logger.error(
                                'Keycloak logout failed after token refresh failure',
                                logoutError
                            );
                        });
                    });
            } catch (error) {
                logger.error(
                    'KeycloakAuthService.scheduleTokenRefresh failed',
                    error
                );
            }
        }, 60 * 1000);
    }

    clearTokenRefresh(): void {
        if (this.refreshIntervalId) {
            clearInterval(this.refreshIntervalId);
            this.refreshIntervalId = null;
        }
    }

    getRolesFromAuthorizationToken(token: string): string[] {
        if (!token) {
            return [];
        }

        try {
            const tokenParts = token.split('.');

            if (tokenParts.length !== 3) {
                logger.warn(
                    'Authorization token has invalid JWT format'
                );
                return [];
            }

            const base64Url = tokenParts[1];
            const base64 = base64Url
                .replace(/-/g, '+')
                .replace(/_/g, '/');

            const paddedBase64 =
                base64 +
                '='.repeat((4 - (base64.length % 4)) % 4);

            const payload = JSON.parse(
                atob(paddedBase64)
            );

            let roles = payload?.role || [];

            if (!Array.isArray(roles)) {
                roles = [roles];
            }

            return roles.filter(
                (role: unknown): role is string =>
                    typeof role === 'string' &&
                    role.trim().length > 0
            );
        } catch (error) {
            logger.error(
                'KeycloakAuthService.getRolesFromAuthorizationToken failed to decode roles',
                error
            );
            return [];
        }
    }

    async getAuthorizationToken(): Promise<void> {
        try {
            const response = await httpService.get<any>(
                ApiEndpoints.getAuthorizationToken
            );

            const authorizationToken =
                response.data?.token ??
                response.data?.Token;

            if (!authorizationToken) {
                throw new Error(
                    'Authorization token was not returned'
                );
            }

            SessionStorageService.setAuthorizationToken(
                authorizationToken
            );
        } catch (error) {
            logger.error(
                'KeycloakAuthService.getAuthorizationToken failed',
                error
            );
            throw error;
        }
    }

    private async forceKeycloakLogout(): Promise<void> {
        try {
            const idToken = SessionStorageService.getIdToken();

            const logoutUrl =
                `${import.meta.env.VITE_KEYCLOAK_URL}` +
                `/realms/${import.meta.env.VITE_KEYCLOAK_REALM}` +
                `/protocol/openid-connect/logout` +
                `?id_token_hint=${encodeURIComponent(idToken ?? '')}` +
                `&post_logout_redirect_uri=${encodeURIComponent(
                    `${window.location.origin}/login`
                )}`;

            this.clearTokenRefresh();
            SessionStorageService.clearAll();

            window.location.href = logoutUrl;
        } catch (error) {
            logger.error(
                'KeycloakAuthService.forceKeycloakLogout failed',
                error
            );
            throw error;
        }
    }
}